import React, { useState, useEffect } from "react";
import MarketingLayout from "../components/MarketingLayout";
import { BRAND } from "../data/brand";
import { PLANS } from "../data/plans";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../hooks/useAuth";
import { useSubscription } from "../../hooks/useSubscription";
import { openRazorpay } from "../../lib/razorpay";

const inr = n => "₹" + n.toLocaleString("en-IN");

function Card({ children, style }) {
  return (
    <div style={{ background: "#08111e", border: "1px solid #131924", borderRadius: 12, padding: "24px", ...style }}>
      {children}
    </div>
  );
}
function SectionLabel({ children }) {
  return <div style={{ fontSize: 9, color: "#2a3a4a", letterSpacing: 2.5, fontWeight: 800, marginBottom: 16, fontFamily: "'JetBrains Mono',monospace" }}>{children}</div>;
}
function Row({ label, value, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #0d1520" }}>
      <span style={{ fontSize: 13, color: "#3d5068" }}>{label}</span>
      <span style={{ fontSize: 13, color: accent || "#8496a8", fontFamily: "monospace", fontWeight: 600 }}>{value}</span>
    </div>
  );
}

// ─── Plan badge ───────────────────────────────────────────────────────────────
function PlanBadge({ plan }) {
  const cfg = {
    free: { color: "#4a5e70", label: "Free" },
    pro:  { color: "#3b82f6", label: "Pro"  },
    team: { color: "#a78bfa", label: "Team" },
  }[plan] || { color: "#4a5e70", label: plan };
  return (
    <span style={{ background: cfg.color + "20", border: `1px solid ${cfg.color}50`, color: cfg.color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>
      {cfg.label}
    </span>
  );
}

// ─── Upgrade cards ────────────────────────────────────────────────────────────
function UpgradeCard({ plan, user, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (!user) { window.location.href = "/app"; return; }
    setBusy(true);
    try {
      await openRazorpay(plan, user, false, onSuccess);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ background: "#09090f", border: `1px solid ${plan.color}30`, borderRadius: 10, padding: "20px", flex: "1 1 220px" }}>
      <div style={{ fontSize: 10, color: plan.accent, fontWeight: 800, letterSpacing: 2, marginBottom: 8, fontFamily: "'JetBrains Mono',monospace" }}>{plan.name.toUpperCase()}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#dde6f0", marginBottom: 16, fontFamily: "'Syne',sans-serif" }}>
        {inr(plan.price)}<span style={{ fontSize: 12, color: "#2a3a4a", fontWeight: 400 }}>/mo</span>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px" }}>
        {plan.features.slice(0, 4).map((f, i) => (
          <li key={i} style={{ fontSize: 12, color: "#4a5e70", marginBottom: 7, display: "flex", gap: 7 }}>
            <span style={{ color: plan.accent }}>✦</span>{f}
          </li>
        ))}
      </ul>
      <button onClick={handle} disabled={busy} style={{ width: "100%", padding: "10px", background: `linear-gradient(90deg,${plan.color},${plan.accent})`, border: "none", color: "#fff", borderRadius: 7, cursor: busy ? "wait" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
        {busy ? "Opening checkout…" : plan.cta}
      </button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AccountPage() {
  const { user, loading: authLoading } = useAuth();
  const { plan, isPro, isTeam, loading: subLoading } = useSubscription(user?.id);
  const [subDetail, setSubDetail] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Load subscription detail
  useEffect(() => {
    if (!supabase || !user) return;
    supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", user.id)
      .eq("status", "active")
      .maybeSingle()
      .then(({ data }) => setSubDetail(data));
  }, [user]);

  // M3 / redirect flicker FIX: use replace() to prevent back-navigation
  // and return null immediately — no render window for unauthenticated users.
  if (!authLoading && !user) {
    window.location.replace("/app");
    return null;
  }

  const handleCancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of your current billing period.")) return;
    setCancelling(true);
    try {
      // C2 FIX: call Edge Function instead of direct supabase.update()
      // The function validates ownership + only allows status → 'cancelled'
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type":  "application/json",
            "Authorization": `Bearer ${session?.access_token}`,
            "apikey":        process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Cancellation failed");
      setCancelDone(true);
      showToast("Subscription cancelled. Access continues until " + new Date(data.period_end).toLocaleDateString("en-IN") + ".");
    } catch (e) {
      showToast("Cancellation failed. Email " + BRAND.supportEmail, "error");
    } finally {
      setCancelling(false);
    }
  };

  const handleUpgradeSuccess = ({ plan_id }) => {
    showToast(`Upgraded to ${plan_id.toUpperCase()} — thank you! Reload to activate.`, "success");
    setTimeout(() => window.location.reload(), 2000);
  };

  if (authLoading || subLoading) return (
    <MarketingLayout>
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 11, color: "#1e2a36", fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2 }}>LOADING…</div>
      </div>
    </MarketingLayout>
  );

  return (
    <MarketingLayout>
      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 72, right: 24, zIndex: 999, background: toast.type === "error" ? "#1c0808" : "#0c2318", border: `1px solid ${toast.type === "error" ? "#f8717140" : "#4ade8040"}`, color: toast.type === "error" ? "#f87171" : "#4ade80", padding: "12px 20px", borderRadius: 8, fontSize: 13, boxShadow: "0 8px 32px #0008", maxWidth: 380, animation: "fadeIn .2s" }}>
          {toast.msg}
        </div>
      )}

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "56px 24px 80px" }}>
        <div style={{ fontSize: 10, color: "#3b82f6", letterSpacing: 3, fontWeight: 800, marginBottom: 10, fontFamily: "'JetBrains Mono',monospace" }}>ACCOUNT</div>
        <h1 style={{ fontSize: "clamp(22px,3vw,32px)", fontWeight: 800, color: "#dde6f0", marginBottom: 40, fontFamily: "'Syne',sans-serif" }}>Your Account</h1>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
          {/* Profile */}
          <Card>
            <SectionLabel>PROFILE</SectionLabel>
            <Row label="Email"   value={user?.email} />
            <Row label="User ID" value={user?.id?.slice(0, 12) + "…"} />
            <Row label="Joined"  value={user?.created_at ? new Date(user.created_at).toLocaleDateString("en-IN") : "—"} />
          </Card>

          {/* Subscription */}
          <Card>
            <SectionLabel>SUBSCRIPTION</SectionLabel>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #0d1520" }}>
              <span style={{ fontSize: 13, color: "#3d5068" }}>Current plan</span>
              <PlanBadge plan={plan} />
            </div>
            {subDetail && (
              <>
                <Row label="Billing"    value={subDetail.billing_period === "annual" ? "Annual" : "Monthly"} />
                <Row label="Renews"     value={new Date(subDetail.current_period_end).toLocaleDateString("en-IN")} accent="#4ade80" />
                <Row label="Payment ID" value={subDetail.razorpay_payment_id?.slice(0, 14) + "…"} />
              </>
            )}
            {!subDetail && plan === "free" && (
              <div style={{ padding: "14px 0", fontSize: 13, color: "#2a3a4a" }}>You're on the free plan — projects sync to this browser only.</div>
            )}
          </Card>
        </div>

        {/* Cancel subscription */}
        {isPro && !cancelDone && (
          <Card style={{ marginBottom: 24 }}>
            <SectionLabel>MANAGE SUBSCRIPTION</SectionLabel>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 14, color: "#8496a8", marginBottom: 4 }}>Cancel subscription</div>
                <div style={{ fontSize: 12, color: "#2a3a4a" }}>You'll keep {plan.toUpperCase()} access until the end of your billing period. No refund unless within 7-day window.</div>
              </div>
              <button onClick={handleCancel} disabled={cancelling}
                style={{ flexShrink: 0, marginLeft: 24, padding: "9px 18px", background: "#140808", border: "1px solid #2a0e0e", color: "#f87171", borderRadius: 6, cursor: cancelling ? "wait" : "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 700 }}>
                {cancelling ? "Cancelling…" : "Cancel plan"}
              </button>
            </div>
          </Card>
        )}

        {/* Upgrade section for free users */}
        {!isPro && (
          <Card style={{ marginBottom: 24 }}>
            <SectionLabel>UPGRADE</SectionLabel>
            <p style={{ fontSize: 14, color: "#4a5e70", marginBottom: 24, lineHeight: 1.7 }}>
              Upgrade to unlock cloud sync across devices, unlimited projects, and 20-version snapshot history.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              {PLANS.filter(p => p.id !== "free").map(p => (
                <UpgradeCard key={p.id} plan={p} user={user} onSuccess={handleUpgradeSuccess} />
              ))}
            </div>
          </Card>
        )}

        {/* Data & privacy */}
        <Card>
          <SectionLabel>DATA & PRIVACY</SectionLabel>
          <p style={{ fontSize: 13, color: "#3d5068", lineHeight: 1.7, marginBottom: 16 }}>
            Your floor plans are your data. To request a full export or permanent deletion of your account and all associated projects, email us.
          </p>
          <a href={`mailto:${BRAND.email}?subject=Data deletion request`}
            style={{ display: "inline-block", padding: "8px 16px", background: "transparent", border: "1px solid #1e2a36", color: "#4a5e70", borderRadius: 6, fontSize: 12, textDecoration: "none" }}>
            Request data deletion
          </a>
        </Card>
      </div>
    </MarketingLayout>
  );
}
